import {
  Box,
  VStack,
  HStack,
  Text,
  Skeleton,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Heading,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Collapse,
  Input,
  Button,
  Select,
  FormControl,
  FormLabel,
  Tag,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { FiFilter, FiCalendar, FiClock, FiMapPin, FiUser, FiCheckCircle, FiXCircle, FiAlertCircle, FiSearch, FiX, FiChevronDown, FiChevronRight } from "react-icons/fi";
import { calcTime, numericFormat } from "../../utils";
import { overtimeUtils } from "../../services/overtimeService";

const ReportTable = ({ employeeData = [], attendanceIsLoading }) => {

  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [overtimeFilter, setOvertimeFilter] = useState(""); // NEW: Overtime filter
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = employeeData || [];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Department filter
    if (departmentFilter) {
      filtered = filtered.filter(item => 
        item?.department === departmentFilter
      );
    }
    
    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(item => 
        item?.attendance_status?.toLowerCase() === statusFilter.toLowerCase() ||
        (statusFilter.toLowerCase() === 'leave' && item?.attendance_status?.toLowerCase() === 'on leave')
      );
    }

    // NEW: Overtime filter
    if (overtimeFilter) {
      filtered = filtered.filter(item => {
        switch (overtimeFilter) {
          case 'with_overtime':
            return overtimeUtils.hasOvertime(item);
          case 'without_overtime':
            return !overtimeUtils.hasOvertime(item);
          case 'early_clockin':
            return item?.overtime_type === 'early_clockin';
          case 'late_clockout':
            return item?.overtime_type === 'late_clockout';
          case 'full_overtime':
            return item?.overtime_type === 'full_overtime';
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [employeeData, searchTerm, departmentFilter, statusFilter, overtimeFilter]);

  // Get unique departments for filter dropdown
  const departments = useMemo(() => {
    if (!Array.isArray(employeeData)) return [];
    const depts = [...new Set(employeeData.map(item => item?.department).filter(Boolean))];
    return depts.sort();
  }, [employeeData]);

  const clearFilters = () => {
    setSearchTerm("");
    setDepartmentFilter("");
    setStatusFilter("");
    setOvertimeFilter(""); // NEW: Clear overtime filter
  };

  const toggleRowExpansion = (employeeId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(employeeId)) {
      newExpandedRows.delete(employeeId);
    } else {
      newExpandedRows.add(employeeId);
    }
    setExpandedRows(newExpandedRows);
  };

  if (attendanceIsLoading) {
    return (
      <Box p={6}>
        <TableContainer>
          <Table variant="simple" w="100%">
            <Thead>
              <Tr>
                <Th></Th>
                <Th>Name</Th>
                <Th>ID</Th>
                <Th>Department</Th>
                <Th>Date</Th>
                <Th>Clock In</Th>
                <Th>Clock Out</Th>
                <Th>Status</Th>
                {/* NEW: Overtime columns */}
              <Th>Shift Hours</Th>
              <Th>Overtime Hours</Th>
              <Th>Overtime Type</Th>
              <Th>Compliance</Th>
              <Th>Pen Count</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <Tr key={i}>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                {/* NEW: Overtime skeleton columns */}
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
                <Td><Skeleton height="20px" /></Td>
              </Tr>
            ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (!attendanceIsLoading && (!Array.isArray(employeeData) || !employeeData.length)) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Box
            p={4}
            borderRadius="50%"
            bg="gray.100"
            color="gray.500"
            fontSize="2xl"
          >
            📊
          </Box>
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="600" color="gray.700">
              No Attendance Data
            </Text>
            <Text color="gray.500" fontSize="sm">
              No attendance records found for the selected period
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box w="100%">
      {/* Table Header */}
      <Flex 
        justifyContent="space-between" 
        alignItems="center" 
        p={6} 
        borderBottom="1px solid" 
        borderColor="gray.200"
        bg={bgColor}
      >
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Attendance Records
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {filteredData?.length || 0} of {employeeData?.length || 0} records
          </Text>
        </VStack>
        <IconButton
          icon={<FiFilter />}
          variant="ghost"
          onClick={onOpen}
          aria-label="Filter reports"
          color="gray.600"
          _hover={{ bg: "gray.100" }}
        />
      </Flex>

      {/* Search Bar */}
      <Box p={4} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
        <HStack spacing={4} align="center">
          <Box flex={1}>
            <HStack spacing={2}>
              <Box position="relative" flex={1}>
                <Input
                  placeholder="Search by name, ID, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  pl={10}
                  pr={searchTerm ? 10 : 4}
                  borderRadius="12px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
                  <FiSearch size={16} />
                </Box>
                {searchTerm && (
                  <Box 
                    position="absolute" 
                    right={3} 
                    top="50%" 
                    transform="translateY(-50%)" 
                    cursor="pointer"
                    onClick={() => setSearchTerm("")}
                    color="gray.400"
                    _hover={{ color: "gray.600" }}
                  >
                    <FiX size={16} />
                  </Box>
                )}
              </Box>
              {(searchTerm || departmentFilter || statusFilter || overtimeFilter) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearFilters}
                  leftIcon={<FiX />}
                  color="gray.600"
                  _hover={{ bg: "gray.200" }}
                >
                  Clear
                </Button>
              )}
            </HStack>
          </Box>
        </HStack>
      </Box>

             {/* Report Table */}
       <TableContainer w="100%">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th></Th>
              <Th>Name</Th>
              <Th>ID</Th>
              <Th>Department</Th>
              <Th>Date</Th>
              <Th>Clock In</Th>
              <Th>Clock Out</Th>
              <Th>Status</Th>
              {/* NEW: Overtime columns */}
              <Th>Shift Hours</Th>
              <Th>Overtime Hours</Th>
              <Th>Overtime Type</Th>
              <Th>Compliance</Th>
              <Th>Pen Count</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData?.map((item, index) => (
              <React.Fragment key={index}>
                <ReportTableRow 
                  {...item} 
                  isExpanded={expandedRows.has(item.employee_id)}
                  onToggle={() => toggleRowExpansion(item.employee_id)}
                />
                <Tr>
                  <Td colSpan={13} p={0}> {/* Updated colspan to include new columns */}
                    <Collapse in={expandedRows.has(item.employee_id)} animateOpacity>
                      <Box bg="gray.50" p={4} borderTop="1px solid" borderColor="gray.200">
                        <VStack spacing={4} align="stretch">
                          <Heading size="sm" color="gray.700" mb={2}>
                            Detailed Attendance Information
                          </Heading>
                          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                            <DetailItem label="Location" value={item.location || "Not specified"} />
                            <DetailItem label="Work Duration" value={item.clock_in_time && item.clock_out_time ? `${calcTime(item.clock_in_time)} - ${calcTime(item.clock_out_time)}` : "Not available"} />
                            <DetailItem label="Date Formatted" value={numericFormat(item.date)} />
                            {/* NEW: Overtime details */}
                            <DetailItem label="Total Hours Worked" value={item.total_hours_worked ? `${item.total_hours_worked.toFixed(2)}h` : "Not available"} />
                            <DetailItem label="Grace Period Used" value={item.grace_period_minutes ? `${item.grace_period_minutes} minutes` : "0 minutes"} />
                            <DetailItem label="Shift Compliance" value={item.shift_compliance ? "Compliant" : "Non-compliant"} />
                          </SimpleGrid>
                        </VStack>
                      </Box>
                    </Collapse>
                  </Td>
                </Tr>
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filter Reports</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              {/* Department Filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  Department
                </FormLabel>
                <Select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  placeholder="All Departments"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500" }}
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  Attendance Status
                </FormLabel>
                                                   <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    placeholder="All Statuses"
                    borderRadius="8px"
                    borderColor="gray.200"
                    _focus={{ borderColor: "blue.500" }}
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="late">Late</option>
                    <option value="leave">Leave / On Leave</option>
                  </Select>
              </FormControl>

              {/* NEW: Overtime Filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                  Overtime Status
                </FormLabel>
                <Select
                  value={overtimeFilter}
                  onChange={(e) => setOvertimeFilter(e.target.value)}
                  placeholder="All Overtime Statuses"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _focus={{ borderColor: "blue.500" }}
                >
                  <option value="with_overtime">With Overtime</option>
                  <option value="without_overtime">Without Overtime</option>
                  <option value="early_clockin">Early Clock-in</option>
                  <option value="late_clockout">Late Clock-out</option>
                  <option value="full_overtime">Full Overtime</option>
                </Select>
              </FormControl>

              {/* Action Buttons */}
              <HStack spacing={3} pt={4}>
                <Button
                  colorScheme="blue"
                  variant="solid"
                  onClick={onClose}
                  size="md"
                  borderRadius="8px"
                  flex={1}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    clearFilters();
                    onClose();
                  }}
                  size="md"
                  borderRadius="8px"
                  flex={1}
                >
                  Clear All
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Report Table Row Component
const ReportTableRow = ({ 
  full_name, 
  employee_id, 
  department, 
  date, 
  clock_in_time, 
  clock_out_time, 
  attendance_status,
  // NEW: Overtime fields
  shift_hours_worked,
  overtime_hours,
  overtime_type,
  shift_compliance,
  // NEW: Auto clock-out fields
  auto_clockout_count,
  isExpanded,
  onToggle
}) => {
  const getAttendanceColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present':
        return 'green';
      case 'absent':
        return 'red';
      case 'late':
        return 'orange';
      case 'on leave':
      case 'leave':
        return 'purple';
      default:
        return 'gray';
    }
  };

  return (
    <Tr 
      cursor="pointer" 
      _hover={{ bg: "gray.50" }}
      onClick={onToggle}
    >
      <Td>
        <IconButton
          icon={isExpanded ? <FiChevronDown /> : <FiChevronRight />}
          variant="ghost"
          size="sm"
          aria-label="Toggle details"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </Td>
      <Td fontWeight="600">{full_name}</Td>
      <Td>{employee_id}</Td>
      <Td>{department}</Td>
      <Td>{numericFormat(date)}</Td>
      <Td>{clock_in_time ? calcTime(clock_in_time) : "Not recorded"}</Td>
      <Td>{clock_out_time ? calcTime(clock_out_time) : "Not recorded"}</Td>
      <Td>
        <Badge
          colorScheme={getAttendanceColor(attendance_status)}
          variant="subtle"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="sm"
        >
          {attendance_status}
        </Badge>
      </Td>
      {/* NEW: Overtime columns */}
      <Td>{shift_hours_worked ? `${shift_hours_worked.toFixed(2)}h` : "-"}</Td>
      <Td>
        {overtime_hours > 0 ? (
          <Text color="red.600" fontWeight="600">
            {overtimeUtils.formatOvertimeHours(overtime_hours)}
          </Text>
        ) : (
          "-"
        )}
      </Td>
      <Td>
        {overtime_type && overtime_type !== 'none' ? (
          <Tag
            colorScheme={overtimeUtils.getOvertimeTypeColor(overtime_type)}
            variant="subtle"
            size="sm"
          >
            {overtimeUtils.getOvertimeTypeDisplay(overtime_type)}
          </Tag>
        ) : (
          "-"
        )}
      </Td>
      <Td>
        <Tag
          colorScheme={shift_compliance ? 'green' : 'red'}
          variant="subtle"
          size="sm"
        >
          {shift_compliance ? 'Compliant' : 'Non-compliant'}
        </Tag>
      </Td>
      <Td>
        {auto_clockout_count !== undefined && auto_clockout_count !== null ? (
          <Text
            fontWeight="600"
            color={
              auto_clockout_count === 0
                ? "gray.500"
                : auto_clockout_count <= 2
                ? "green.600"
                : auto_clockout_count <= 4
                ? "orange.600"
                : "red.600"
            }
            fontSize="sm"
          >
            {auto_clockout_count}
          </Text>
        ) : (
          <Text color="gray.400">-</Text>
        )}
      </Td>
    </Tr>
  );
};

// Detail Item Component
const DetailItem = ({ label, value }) => {
  return (
    <VStack spacing={1} align="start">
      <Text fontSize="xs" color="gray.500" fontWeight="500">
        {label}
      </Text>
      <Text fontSize="sm" color="gray.900" fontWeight="600">
        {typeof value === 'object' && value !== null 
          ? (value.name || value.department_name || JSON.stringify(value))
          : (value || "Not available")
        }
      </Text>
    </VStack>
  );
};

export default ReportTable;
  